import React, { useState, useEffect } from "react";
import { Navbar, Container, Form, InputGroup } from "react-bootstrap";
import { FaBars, FaSearch, FaBell, FaCog, FaMoon, FaSun, FaChevronDown, FaExchangeAlt, FaCross, FaTimes, FaSpinner, FaBan, FaCommentDots } from "react-icons/fa";
import SearchDrawer from "./searchdrawer";
import FeedbackModal from "../FeedbackModal";
import { fetchClients, fetchNotificationCount, fetchUnrespondedTickets } from "../../api/overviewApi";
import { FaUserAlt } from "react-icons/fa";
import { CgArrowsExchangeAltV } from "react-icons/cg";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ROUTES } from "../../routes.js";
import { getClientHomeRoute } from "../../utils/clientMsp";
const headerColors = {
  menu: {
    tint: "rgba(59,130,246,0.10)",
    border: "rgba(59,130,246,0.35)",
    icon: "#3b82f6",
  },
  search: {
    tint: "rgba(16,185,129,0.10)",
    border: "rgba(16,185,129,0.35)",
    icon: "#10b981",
  },
  notifications: {
    tint: "rgba(111,66,193,0.10)",
    border: "rgba(111,66,193,0.35)",
    icon: "#6f42c1",
  },
  theme: {
    tint: "rgba(245,158,11,0.15)",
    border: "rgba(245,158,11,0.35)",
    icon: "#f59e0b",
  },
  settings: {
    tint: "rgba(239,68,68,0.10)",
    border: "rgba(239,68,68,0.35)",
    icon: "#ef4444",
  },
};

const baseIconButton = {
  width: 40,
  height: 40,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all .25s ease",

  // 3D look
  boxShadow: `
    0 2px 4px rgba(0,0,0,0.15),
    inset 0 1px 0 rgba(255,255,255,0.15)
  `,
  background: "var(--color-bg-card)",
  border: "1px solid var(--color-border)",
};

const themedIconButton = (theme, active = false) => ({
  ...baseIconButton,
  background: active ? theme.tint : theme.tint,
  border: `1px solid ${theme.border}`,
  boxShadow: active
    ? `
      0 3px 8px ${theme.border},
      inset 0 1px 2px rgba(255,255,255,0.3)
    `
    : baseIconButton.boxShadow,
  transform: active ? "translateY(-1px)" : "none",
});

const Header = ({ isDark, toggleDark, onToggleSidebar, notificationsOpen, onToggleNotifications, selectedClient, onClientSelect, SetMode, mode, user }) => {
  const { isStaffUser, logout: logoutUser, isAuthenticated } = useAuth();
  const [showSearchDrawer, setShowSearchDrawer] = useState(false);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loadingClients, setLoadingClients] = useState(true);
  const [activeClientIndex, setActiveClientIndex] = useState(null);
  const [hoveredDisabledClientIndex, setHoveredDisabledClientIndex] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const clientDropdownRef = React.useRef(null);
  const isMobile = window.innerWidth <= 992;
  const location = useLocation();
  const navigate = useNavigate();

  // Are we in client view?
  const isClientView = location.pathname.startsWith("/client");
  
  // Check if user is superadmin
  const isStaff = isStaffUser();
  
  // State to store client name fetched by client_id
  const [fetchedClientName, setFetchedClientName] = React.useState("");
  
  // Get client name for normal users - memoized to avoid unnecessary recalculations
  const clientName = React.useMemo(() => {
    // Try selectedClient first (set in App.js for normal users)
    if (selectedClient?.client_name) {
      return selectedClient.client_name;
    }
    
    // Try user.client.client_name (from login response)
    // Handle both null and undefined explicitly - check if it's a valid string
    const nameFromUser = user?.client?.client_name;
    if (nameFromUser && typeof nameFromUser === 'string' && nameFromUser.trim() !== '') {
      return nameFromUser;
    }
    
    // Try fetched client name (from client_id lookup)
    if (fetchedClientName && typeof fetchedClientName === 'string' && fetchedClientName.trim() !== '') {
      return fetchedClientName;
    }
    
    // Fallback: try to get from user object directly (in case structure is different)
    if (user?.client_name && typeof user.client_name === 'string') {
      return user.client_name;
    }
    
    return "";
  }, [selectedClient, user, fetchedClientName]);

  const isClientAccount = !isStaff;
  
  // Fetch client name by client_id if we have client_id but no client_name
  React.useEffect(() => {
    // Only for normal users
    if (isStaff) return;
    
    // If we already have a client name, don't fetch
    if (clientName) return;
    
    // Get client_id from user
    const clientId = user?.client?.client_id || user?.client_id || selectedClient?.client_id;
    
    if (clientId && clients.length > 0) {
      // Find client in the already-fetched clients list
      const foundClient = clients.find(c => c.client_id === clientId);
      if (foundClient?.client_name) {
        setFetchedClientName(foundClient.client_name);
        // Also update selectedClient if it's not set
        if (!selectedClient && onClientSelect) {
          onClientSelect(foundClient);
        }
      }
    }
  }, [user, selectedClient, clients, clientName, isStaff, onClientSelect]);

  useEffect(() => {
    let isMounted = true;
    let cancelled = false;

    setLoadingClients(true);

    // Fetch clients on mount
    fetchClients()
      .then((clientsList) => {
        if (!cancelled && isMounted) {
          console.log("Fetched clients:", clientsList);
          if (clientsList && clientsList.length > 0) {
            setClients(clientsList);
          }
          setLoadingClients(false);
        }
      })
      .catch((error) => {
        if (!cancelled && isMounted) {
          // Only log error, don't clear existing clients
          if (error.code !== "ECONNABORTED") {
            console.error("Failed to fetch clients:", error);
          }
          setLoadingClients(false);
        }
      });

    return () => {
      cancelled = true;
      isMounted = false;
    };
  }, []);

  const filteredClients = React.useMemo(() => {
    return clients.filter((client) => {
      const q = (searchQuery || "").trim().toLowerCase();
      if (!q) return true;

      const name = (client?.client_name || "").toLowerCase();
      const id = String(client?.client_id ?? "").toLowerCase();

      return name.includes(q) || id.includes(q);
    });
  }, [clients, searchQuery]);

  const handleClientSelect = (client) => {
    onClientSelect(client);
    setShowDropdown(false);
    setSearchQuery("");
    setActiveClientIndex(null);
    setHoveredDisabledClientIndex(null);
    navigate(getClientHomeRoute(client, { isStaff, isClientView: mode === "client" }));
  };

  const handleClientSearchKeyDown = (e) => {
    if (!isStaff) return;

    const count = filteredClients.length;
    if (count === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!showDropdown) setShowDropdown(true);
      setActiveClientIndex((prev) => {
        const start = prev == null ? -1 : prev;
        return Math.min(start + 1, count - 1);
      });
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!showDropdown) setShowDropdown(true);
      setActiveClientIndex((prev) => {
        const start = prev == null ? count : prev;
        return Math.max(start - 1, 0);
      });
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (activeClientIndex == null) {
        setActiveClientIndex(0);
      }

      const indexToSelect = activeClientIndex == null ? 0 : activeClientIndex;
      const client = filteredClients[indexToSelect];
      if (!client) return;

      const hasNoId = client.client_id == null || client.client_id === 0 || client.client_id === "";
      if (hasNoId) {
        setHoveredDisabledClientIndex(indexToSelect);
        return;
      }

      handleClientSelect(client);
    }
  };

  // When the dropdown opens (or the search query changes), set an initial keyboard highlight.
  useEffect(() => {
    if (!isStaff || !showDropdown) return;
    if (loadingClients) return;

    if (filteredClients.length === 0) {
      setActiveClientIndex(null);
      return;
    }

    const selectedIndex =
      selectedClient?.client_id != null
        ? filteredClients.findIndex((c) => c.client_id === selectedClient.client_id)
        : -1;

    setActiveClientIndex(selectedIndex !== -1 ? selectedIndex : 0);
  }, [isStaff, showDropdown, loadingClients, searchQuery, filteredClients, selectedClient?.client_id]);

  // Keep the "disabled" red icon aligned with the keyboard highlight.
  useEffect(() => {
    if (!showDropdown) return;
    if (activeClientIndex == null) {
      setHoveredDisabledClientIndex(null);
      return;
    }

    const client = filteredClients[activeClientIndex];
    if (!client) return;
    const hasNoId = client.client_id == null || client.client_id === 0 || client.client_id === "";
    setHoveredDisabledClientIndex(hasNoId ? activeClientIndex : null);
  }, [activeClientIndex, showDropdown, filteredClients]);

  // Keep the keyboard-highlighted client visible inside the scrollable dropdown.
  useEffect(() => {
    if (!showDropdown || activeClientIndex == null) return;

    const list = clientDropdownRef.current;
    if (!list) return;

    const option = list.querySelector(`#client-option-${activeClientIndex}`);
    if (!option) return;

    option.scrollIntoView({ block: "nearest" });
  }, [activeClientIndex, showDropdown]);

  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector("[data-client-search]");
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowDropdown(false);
        setActiveClientIndex(null);
        setHoveredDisabledClientIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  // useEffect(() => {
  //   const updateNotificationCount = async () => {
  //     const clientId = selectedClient?.client_id || null;
  //     const count = await fetchNotificationCount(clientId);
  //     setNotificationCount(count);
  //   };

  //   updateNotificationCount();
    
  //   const interval = setInterval(updateNotificationCount, 30000);
    
  //   return () => clearInterval(interval);
  // }, [selectedClient]);

  // useEffect(() => {
  //   if (!notificationsOpen) {
  //     const updateNotificationCount = async () => {
  //       const clientId = selectedClient?.client_id || null;
  //       const count = await fetchNotificationCount(clientId);
  //       setNotificationCount(count);
  //     };
  //     updateNotificationCount();
  //   }
  // }, [notificationsOpen, selectedClient]);

  useEffect(() => {
    if (!isAuthenticated) {
      // User is not logged in: skip API call
      setNotificationCount(0);
      return;
    }
  
    const updateNotificationCount = async () => {
      try {
        const clientId = selectedClient?.client_id || null;
        const count = await fetchNotificationCount(clientId);
        // Add unresponded tickets count to badge
        let unrespondedCount = 0;
        if (clientId) {
          const unrespondedRes = await fetchUnrespondedTickets(clientId, 2);
          unrespondedCount = unrespondedRes.total ?? unrespondedRes.tickets?.length ?? 0;
        }
        setNotificationCount(count + unrespondedCount);
      } catch (err) {
        // Non-critical: badge shows 0. Avoid console spam from polling/connection resets.
        setNotificationCount(0);
      }
    };
  
    updateNotificationCount();
  
    const interval = setInterval(updateNotificationCount, 30000);

    // Re-fetch count when user marks all as read — emergency pinned tickets may still be active
    const handleNotificationsRead = () => updateNotificationCount();
    window.addEventListener('notificationsRead', handleNotificationsRead);

    // Single non-emergency ticket viewed: decrement badge by 1
    const handleTicketRead = () => setNotificationCount(prev => Math.max(0, prev - 1));
    window.addEventListener('notificationTicketRead', handleTicketRead);

    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationsRead', handleNotificationsRead);
      window.removeEventListener('notificationTicketRead', handleTicketRead);
    };
  }, [selectedClient, isAuthenticated]);
  
  useEffect(() => {
    if (!notificationsOpen || !isAuthenticated) return;
  
    const updateNotificationCount = async () => {
      try {
        const clientId = selectedClient?.client_id || null;
        const count = await fetchNotificationCount(clientId);
        setNotificationCount(count);
      } catch (err) {
        setNotificationCount(0);
      }
    };
  
    updateNotificationCount();
  }, [notificationsOpen, selectedClient, isAuthenticated]);

  
  /* ---------------- PROFILE DROPDOWN UI ---------------- */
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const toggleProfileMenu = () => setShowProfileMenu((prev) => !prev);

  useEffect(() => {
    if (!showProfileMenu) return;

    const handleClick = (e) => {
      const menu = document.querySelector("#profile-menu");
      const button = document.querySelector("#profile-button");
      if (menu && !menu.contains(e.target) && !button.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showProfileMenu]);

  return (
    <>
      <style>{`
        .dark-mode .client-dropdown-list::-webkit-scrollbar { width: 6px; }
        .dark-mode .client-dropdown-list::-webkit-scrollbar-track { background: transparent; }
        .dark-mode .client-dropdown-list::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 3px; }
        .dark-mode .client-dropdown-list::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.28); }
        .dark-mode .client-dropdown-list { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.2) transparent; }
      `}</style>
      {/* SEARCH DRAWER */}
      <SearchDrawer open={showSearchDrawer} onClose={() => setShowSearchDrawer(false)} clients={clients} selectedClient={selectedClient} onClientSelect={handleClientSelect} />

      {/* FEEDBACK MODAL */}
      <FeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        userEmail={user?.email || ""}
        clientName={clientName || ""}
      />

      <Navbar
        className="header px-3 position-sticky top-0 start-0"
        expand="lg"
        style={{
          height: 72,
          zIndex: 99,
          background: "var(--color-bg-header)",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <Container fluid style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 8 }}>
          {/* LEFT: hamburger + client search */}
          <div className="d-flex align-items-center gap-2">
            {/* Mobile-only hamburger */}
            {isMobile && (
              <button onClick={onToggleSidebar} style={themedIconButton(headerColors.menu)}>
                <FaBars size={18} color={headerColors.menu.icon} />
              </button>
            )}

            {/* MOBILE SEARCH ICON */}
            {isMobile && isStaff && !isClientView && (
              <button onClick={() => setShowSearchDrawer(true)} style={themedIconButton(headerColors.search)}>
                <FaSearch size={18} color={headerColors.search.icon} />
              </button>
            )}
            {!isMobile && isStaff && !isClientView && (
              <div style={{ flex: 1, maxWidth: 450, margin: "0 40px", position: "relative" }} data-client-search>
                <InputGroup className="shadow-sm rounded-4">
                  <InputGroup.Text
                    style={{
                      background: headerColors.search.tint,
                      border: `1px solid ${headerColors.search.border}`,
                      borderTopLeftRadius: 14,
                      borderBottomLeftRadius: 14,
                    }}
                  >
                    <FaSearch size={16} color={headerColors.search.icon} />
                  </InputGroup.Text>

                  <Form.Control
                    placeholder={
                      loadingClients
                        ? "Loading clients..."
                        : isStaff
                        ? (selectedClient?.client_name || "Search clients...")
                        : clientName || "Your client"
                    }
                    value={
                      isStaff
                        ? searchQuery 
                        : ""
                    }
                    onChange={(e) => {
                      if (isStaff) {
                        setSearchQuery(e.target.value);
                        if (clients.length > 0 && !loadingClients) {
                          setShowDropdown(true);
                        }
                      }
                    }}
                    onKeyDown={handleClientSearchKeyDown}
                    onFocus={() => {
                      if (isStaff && clients.length > 0 && !loadingClients) {
                        setShowDropdown(true);
                      }
                    }}
                    aria-expanded={showDropdown}
                    aria-activedescendant={activeClientIndex != null ? `client-option-${activeClientIndex}` : undefined}
                    disabled={loadingClients || !isStaff}
                    readOnly={!isStaff}
                    style={{
                      background: "var(--color-bg-card)",
                      border: `1px solid ${headerColors.search.border}`,
                      borderTopRightRadius: 14,
                      borderBottomRightRadius: 14,
                      color: "var(--color-text)",
                      opacity: loadingClients || !isStaff ? 0.7 : 1,
                      cursor: loadingClients || !isStaff ? "not-allowed" : "text",
                      boxShadow: `
      0 2px 4px rgba(0,0,0,0.15),
      inset 0 1px 0 rgba(255,255,255,0.15)
    `,
                    }}
                  />
                  {selectedClient && isStaff && (
                    <InputGroup.Text
                      onClick={(e) => {
                        e.stopPropagation();
                        onClientSelect(null);
                        setShowDropdown(false);
                        setSearchQuery("");
                        setActiveClientIndex(null);
                        setHoveredDisabledClientIndex(null);
                      }}
                      style={{
                        border: "none",
                        borderTopRightRadius: 14,
                        borderBottomRightRadius: 14,
                        cursor: "pointer",
                        background: "var(--color-bg-card)",
                      }}
                    >
                      <FaTimes size={12} color="var(--color-text-light)" />
                    </InputGroup.Text>
                  )}
                </InputGroup>

                {showDropdown && isStaff && (
                  <div
                    ref={clientDropdownRef}
                    className="client-dropdown-list"
                    onMouseLeave={() => {
                      setActiveClientIndex(null);
                      setHoveredDisabledClientIndex(null);
                    }}
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      marginTop: 8,
                      background: "var(--color-bg-card)",
                      borderRadius: 14,
                      boxShadow: "var(--shadow-md)",
                      maxHeight: 300,
                      overflowY: "auto",
                      zIndex: 1000,
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    {loadingClients ? (
                      <div style={{ padding: "12px 16px", color: "var(--color-text-light)", textAlign: "center" }}>Loading clients...</div>
                    ) : filteredClients.length === 0 ? (
                      <div style={{ padding: "12px 16px", color: "var(--color-text-light)", textAlign: "center" }}>
                        {clients.length === 0 ? "No clients available" : "No clients match your search"}
                      </div>
                    ) : (
                      filteredClients.map((client, index) => {
                        const hasNoId = client.client_id == null || client.client_id === 0 || client.client_id === "";
                        const showRedIcon = hasNoId && hoveredDisabledClientIndex === index;
                        const isActive = activeClientIndex === index;
                        return (
                          <div
                            key={client.client_id != null && client.client_id !== "" ? client.client_id : `client-${index}`}
                            onClick={() => !hasNoId && handleClientSelect(client)}
                            id={`client-option-${index}`}
                            style={{
                              padding: "12px 16px",
                              cursor: hasNoId ? "not-allowed" : "pointer",
                              borderBottom: "1px solid var(--color-border)",
                              transition: "background 0.2s",
                              opacity: hasNoId ? 0.5 : 1,
                              background: isActive ? "color-mix(in srgb, var(--color-text-light) 6%, var(--color-bg-card))" : "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                            onMouseEnter={(e) => {
                              setActiveClientIndex(index);
                              if (hasNoId) setHoveredDisabledClientIndex(index);
                              else setHoveredDisabledClientIndex(null);
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontWeight: isActive ? 700 : 500,
                                  color: hasNoId ? "var(--color-text-light)" : "var(--color-text)",
                                }}
                              >
                                {client.client_name}
                              </div>
                              <div style={{ fontSize: 12, color: "var(--color-text-light)", marginTop: 2 }}>ID: {client.client_id ?? "—"}</div>
                            </div>
                            {showRedIcon && <FaBan size={18} color="#e74a3b" style={{ flexShrink: 0 }} />}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

          </div>

          {/* RIGHT ICON GROUP */}
          <div className="d-flex align-items-center gap-2" style={{ justifyContent: "flex-end" }}>
            {/* GIVE FEEDBACK BUTTON - visible for all authenticated users */}
            {(
              <button
                onClick={() => setShowFeedbackModal(true)}
                style={{
                  borderRadius: 12,
                  border: "1px solid #17a2b8",
                  background: "rgba(23,162,184,0.10)",
                  color: "#17a2b8",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "var(--shadow-sm)",
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                }}
                className="px-lg-3 py-2"
              >
                <FaCommentDots size={14} />
                {!isMobile && "Give Feedback"}
              </button>
            )}

            {/* SWITCH TO CLIENT VIEW BUTTON - Only show for superadmin */}
            {isStaff && !isClientView && (
              <button
                onClick={() => {
                  SetMode("client");
                  navigate(getClientHomeRoute(selectedClient, { isStaff: true, isClientView: true }));
                }}
                style={{
                  borderRadius: 12,
                  border: "1px solid #6f42c1",
                  background: "rgba(111,66,193,0.12)",
                  color: "#6f42c1",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "var(--shadow-sm)",
                }}
                className="px-lg-3 py-2"
              >
                <FaUserAlt size={14} />
                {!isMobile ? "Switch to Client View" : <CgArrowsExchangeAltV size={20} />}
              </button>
            )}

            {/* SWITCH TO ADMIN VIEW BUTTON - Only show for superadmin */}
            {isStaff && isClientView && (
              <button
                onClick={() => {
                  SetMode("superadmin");
                  navigate(ROUTES.SUPERADMIN_OVERVIEW);
                }}
                style={{
                  borderRadius: 12,
                  border: "1px solid #4e73df",
                  background: "rgba(78,115,223,0.12)",
                  color: "#4e73df",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "var(--shadow-sm)",
                }}
                className="px-lg-3 py-2"
              >
                <FaUserAlt size={14} />
                {!isMobile ? "Switch to Admin" : <CgArrowsExchangeAltV size={20} />}
              </button>
            )}
            <div style={{ position: "relative" }}>
              <button onClick={onToggleNotifications} style={themedIconButton(headerColors.notifications, notificationsOpen)}>
                <FaBell size={18} color={notificationsOpen ? headerColors.notifications.icon : headerColors.notifications.icon} />
              </button>
              {notificationCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    backgroundColor: "#ef4444",
                    color: "white",
                    borderRadius: "50%",
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: "bold",
                    border: "2px solid var(--color-bg-card)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                >
  {notificationCount > 99 ? "99+" : notificationCount}

                </span>
              )}
            </div>
            <button onClick={toggleDark} style={themedIconButton(headerColors.theme)}>
              {isDark ? <FaSun size={18} color={headerColors.theme.icon} /> : <FaMoon size={18} color={headerColors.theme.icon} />}
            </button>

            <div style={{ position: "relative" }}>
              {/* PROFILE BUTTON */}
              <div
                id="profile-button"
                style={{
                  height: 40,
                  overflow: "hidden",
                  background: "rgba(239,68,68,0.10)",
                  color: "#ef4444",
                  boxShadow: "var(--shadow-sm)",
                  border: "1px solid rgba(239,68,68,0.35)",
                  cursor: "pointer",
                  borderRadius: 50,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,

                  transition: "0.25s",
                }}
                className="p-3 pe-0"
                onClick={toggleProfileMenu}
              >
                <span className="fs-6">
                  {(() => {
                    // Helper function to get initials (first 2 letters)
                    const getInitials = (name) => {
                      if (!name || typeof name !== 'string') return '';
                      const words = name.trim().split(/\s+/);
                      if (words.length === 0) return '';
                      
                      if (words.length === 1) {
                        // Single word: take first 2 letters
                        return name.substring(0, 2).toUpperCase();
                      } else {
                        // Multiple words: take first letter of first 2 words
                        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
                      }
                    };

                    // For normal users, show client name initials
                    if (!isStaff && clientName) {
                      const initials = getInitials(clientName);
                      return initials ? `Hi ${initials}` : "Hi";
                    }
                    // For superadmin, show first name initials or "Admin"
                    if (user?.first_name && user?.last_name) {
                      const initials = getInitials(user.first_name + ' ' + user.last_name);
                      return initials ? `Hi ${initials}` : "Hi User";
                    }
                    return "Hi User";
                  })()}
                </span>

                <img 
                  src="/static/images/profile.png" 
                  alt="user" 
                  onError={(e) => {
                    e.target.src = "https://i.pravatar.cc/300?img=68";
                  }}
                  style={{ width: 36, height: 36, objectFit: "cover", borderRadius: "50%" }} 
                  className="border border-dark" 
                />

                {/* <FaChevronDown size={14} className="ms-1" /> */}
              </div>

              {/* PROFILE MENU */}
              {showProfileMenu && (
                <div
                  id="profile-menu"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "110%",
                    width: 260,
                    background: "var(--color-bg-card)",
                    borderRadius: 16,
                    padding: 16,
                    boxShadow: "var(--shadow-md)",
                    border: "1px solid var(--color-border)",
                    zIndex: 2000,
                    animation: "fadeIn 0.2s ease",
                  }}
                >
                  {/* TOP SECTION */}
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <img 
                      src="/static/images/profile.png" 
                      alt="profile" 
                      onError={(e) => {
                        e.target.src = "https://i.pravatar.cc/300?img=68";
                      }}
                      style={{ width: 60, height: 60, borderRadius: "50%", objectFit: "cover", border: "2px solid var(--color-border)" }} 
                    />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h6 className="m-0" style={{ color: "var(--color-text)" }}>
                        {user?.full_name || user?.first_name + " " + (user?.last_name || "") || "User"}
                      </h6>
                      <div style={{ 
                        fontSize: 14, 
                        fontWeight: 600,
                        color: "var(--color-text)",
                        marginTop: 2
                      }}>
                        {user?.is_superuser ? "Super Admin" : user?.is_staff ? "Staff" : "Client User"}
                      </div>
                      {/* Show username (email) for normal users - truncated */}
                      {!isStaff && user?.email && (
                        <div style={{ 
                          fontSize: 11, 
                          color: "var(--color-text-light)", 
                          marginTop: 4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%"
                        }}>
                          {user.email.length > 25 ? user.email.substring(0, 25) + "..." : user.email}
                        </div>
                      )}
                    </div>
                  </div>

                  <hr />

                  {/* MENU OPTIONS */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    <div
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate(ROUTES.PROFILE);
                      }}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "var(--color-bg)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        transition: "0.25s",
                      }}
                    >
                      <FaCog size={16} color="var(--color-text-light)" />
                      <span style={{ color: "var(--color-text)" }}>Settings</span>
                    </div>
                  </div>

                  {/* LOGOUT BUTTON AT BOTTOM */}
                  <div className="mt-3 pt-2" style={{ borderTop: "1px solid var(--color-border)" }}>
                    <button
                      onClick={async () => {
                        if (isLoggingOut) return; // Prevent multiple clicks
                        setIsLoggingOut(true);
                        try {
                          await logoutUser();
                          setShowProfileMenu(false);
                          navigate(ROUTES.LOGIN);
                        } catch (error) {
                          console.error("Logout error:", error);
                          // Still navigate to login even if logout API fails
                          navigate(ROUTES.LOGIN);
                        } finally {
                          setIsLoggingOut(false);
                        }
                      }}
                      disabled={isLoggingOut}
                      style={{
                        width: "100%",
                        padding: "10px",
                        borderRadius: 12,
                        background: isLoggingOut ? "rgba(239,68,68,0.25)" : "rgba(239,68,68,0.15)",
                        border: "1px solid rgba(239,68,68,0.35)",
                        color: "#ef4444",
                        fontWeight: 600,
                        cursor: isLoggingOut ? "not-allowed" : "pointer",
                        transition: "0.25s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        opacity: isLoggingOut ? 0.8 : 1,
                      }}
                    >
                      {isLoggingOut && (
                        <FaSpinner 
                          size={14} 
                          className="spinning"
                        />
                      )}
                      {isLoggingOut ? "Logging out..." : "Logout"}
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* <button style={themedIconButton(headerColors.settings)}>
              <FaCog size={18} color={headerColors.settings.icon} />
            </button> */}
          </div>
        </Container>
      </Navbar>
    </>
  );
};

const iconButtonStyle = {
  width: 40,
  height: 40,
  borderRadius: 12,
  border: "none",
  background: "var(--color-bg-card)",
  boxShadow: "var(--shadow-sm)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export default Header;
